import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from '@monorepo/nova-ui';
import { FuturesSymbolWithMarket } from '@hub/services/entities/futures-symbols-service';
import { SUPPORTED_SYMBOLS_MODAL_CONSTANTS } from '../constants';
import { EnrichedSymbol } from '../types';

const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const enrichSymbol = (item: FuturesSymbolWithMarket): EnrichedSymbol => ({
  code: item.symbol,
  displayName: item.symbol,
  market: item.market,
});

export const useSupportedSymbolsSearch = (
  symbols: FuturesSymbolWithMarket[],
) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(
    searchTerm.trim(),
    SUPPORTED_SYMBOLS_MODAL_CONSTANTS.SEARCH_DEBOUNCE_MS,
  );

  const enrichedSymbols = useMemo(() => symbols.map(enrichSymbol), [symbols]);

  const filteredSymbols = useMemo(() => {
    if (!debouncedSearch) return enrichedSymbols;

    const pattern = new RegExp(escapeRegex(debouncedSearch), 'i');

    return enrichedSymbols.filter(
      (symbol) =>
        pattern.test(symbol.code) ||
        pattern.test(symbol.displayName) ||
        pattern.test(symbol.market),
    );
  }, [enrichedSymbols, debouncedSearch]);

  const handleSearchChange = useCallback(
    (value: string) => setSearchTerm(value),
    [],
  );

  const handleClearSearch = useCallback(() => setSearchTerm(''), []);

  return {
    searchTerm,
    filteredSymbols,
    hasNoResults: debouncedSearch.length > 0 && filteredSymbols.length === 0,
    handleSearchChange,
    handleClearSearch,
  };
};
